import { AstNode, FieldTypeResolver, FormulaError, ValueType } from './types';
import { EvalValue, ListItem } from './values';

/**
 * What an argument position takes. 'expr' is any expression; the others only
 * parse as a bracket ref (list, member, kpi, token) or as LAMBDA(…) (lambda),
 * and the parser, typecheck and editor all read that from here.
 */
export type ParamKind = 'expr' | 'list' | 'member' | 'kpi' | 'token' | 'lambda';

/**
 * What an expr param accepts: one value type, anything scalar ('any'), or
 * the call's shared type ('same' — every 'same' param and a 'same' return
 * agree on one type, IF's branches and MIN's arguments alike).
 */
export type ParamType = ValueType | 'any' | 'same';

export interface ParamSpec {
  name: string;
  kind: ParamKind;
  /** kind 'expr' (and a 'same'-typed member): the type accepted here. Defaults to 'any'. */
  type?: ParamType;
  optional?: boolean;
  /** This param and every rest param after it repeat as one group, indefinitely. */
  rest?: boolean;
  /** kind 'token': the canonical spellings, matched case-insensitively. */
  values?: readonly string[];
  /** kind 'member': the member (of the preceding list param) must be number-typed. */
  numeric?: boolean;
  /** Parser message when a bracket ref is expected here and something else was typed. */
  expect?: string;
  /** Parser message when this param is missing at the closing ")" (INDEX's third). */
  missing?: string;
  /** Typecheck hint appended when the wrong kind of field lands here (SUM's "use + instead"). */
  hint?: string;
}

/** An evaluated argument, shaped by its param kind. */
export type CallArg =
  | { kind: 'value'; value: EvalValue }
  | { kind: 'list'; ref: string; items: ListItem[] }
  | { kind: 'member'; ref: string; list: string; key: string }
  | { kind: 'ref'; ref: string };

export interface EvalCallContext {
  resolveField: (key: string) => unknown;
  /** A raw row value as the engine reads it for `ref`: blank-normalized and converted to the ref's declared type. */
  read: (raw: unknown, ref: string) => EvalValue | FormulaError;
  asOfDate?: string;
}

export interface CheckCallContext {
  lookup: FieldTypeResolver;
  /** The next field-ref index in source order; every bracket ref the check consumes takes one. */
  nextField: () => number;
}

export interface FunctionSpec {
  name: string;
  params: ParamSpec[];
  /** The result type, or the call's shared type. */
  returns: ValueType | 'same';
  /** What the shared type may be; unrestricted when absent. */
  sameOf?: ValueType[];
  /** As shown to users: 'ROUND(value, digits?)'. Also the usage the parser quotes. */
  signature: string;
  doc: string;
  /**
   * What a blank argument does. 'propagate' (the default): the call is blank
   * without running. 'handle': the evaluator sees the blank — ISBLANK's whole
   * job, or a function with no value arguments at all.
   */
  blanks?: 'propagate' | 'handle';
  /**
   * Plain functions receive their arguments already evaluated (errors already
   * propagated, blanks too unless the entry handles them). Absent for the
   * special forms the core evaluates itself: IF, IFERROR, IFBLANK, REDUCE,
   * and the LET and LAMBDA nodes.
   */
  evaluate?: (args: CallArg[], ctx: EvalCallContext) => EvalValue | FormulaError;
  /** A static rule the params can't state; runs in place of the generic check for kpi/token params. */
  check?: (args: AstNode[], ctx: CheckCallContext) => void;
}

export interface FormulaRegistry {
  get(name: string): FunctionSpec | undefined;
  has(name: string): boolean;
  /** Every name, in registration order. */
  names(): string[];
}

/** Names match case-insensitively, like function names in a formula. */
export function createRegistry(specs: FunctionSpec[]): FormulaRegistry {
  const byName = new Map(specs.map((spec) => [spec.name.toUpperCase(), spec]));
  return {
    get: (name) => byName.get(name.toUpperCase()),
    has: (name) => byName.has(name.toUpperCase()),
    names: () => [...byName.values()].map((spec) => spec.name),
  };
}

/** How many arguments a call takes: rest params count once toward the minimum and lift the maximum. */
export function arity(spec: FunctionSpec): { min: number; max: number } {
  let min = 0;
  let max = 0;
  for (const param of spec.params) {
    if (!param.optional) {
      min++;
    }
    max++;
    if (param.rest) {
      max = Infinity;
    }
  }
  return { min, max };
}

/** The params that repeat, in order — empty when the call is fixed-arity. */
export function restGroup(spec: FunctionSpec): ParamSpec[] {
  return spec.params.filter((param) => param.rest);
}

/** The param an argument position lands on, cycling through the rest group past the fixed params. */
export function paramAt(spec: FunctionSpec, index: number): ParamSpec | undefined {
  if (index < spec.params.length) {
    return spec.params[index];
  }
  const group = restGroup(spec);
  if (group.length === 0) {
    return undefined;
  }
  return group[(index - spec.params.length) % group.length];
}
